import React from "react";
import { Check, Star, AlertTriangle, Minus, ArrowDown } from "lucide-react";
import { WhatsAppIcon } from "../multichannel/iconesCanais";

/**
 * MioloNo — o interior do card.
 *
 * É isso que separa esta seção de um fluxograma: em vez de caixas iguais com
 * texto, cada etapa mostra um pedaço da operação real.
 *
 * Um mesmo card pode assumir mini-interfaces diferentes conforme o negócio: o
 * terceiro passo é lista de estoque na pizzaria, agenda no salão, ordem de
 * serviço na assistência e painel de conexão no provedor. Por isso o miolo
 * despacha por `conteudo.layout` — o dado escolhe a interface — e só cai no
 * mapa por `tipo` quando o layout não é declarado.
 *
 * Sem `conteudo`, mostra a descrição simples (fallback).
 */

const AVATAR = "/robo-olhos-abertos.webp";

const CAIXA = {
  background: "rgba(255,255,255,0.035)",
  border: "1px solid rgba(255,255,255,0.06)",
};

function Balao({ children, propria = false }) {
  return (
    <div
      className="rounded-lg px-2 py-1.5 text-[11.5px] leading-snug"
      style={{
        background: propria ? "rgba(34,197,94,0.12)" : "rgba(255,255,255,0.05)",
        border: `1px solid ${propria ? "rgba(34,197,94,0.22)" : "rgba(255,255,255,0.07)"}`,
        color: "#e4e4e7",
      }}
    >
      {children}
    </div>
  );
}

/* ---------------- conversa do cliente ---------------- */
function Conversa({ c }) {
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center gap-1.5">
        <span className="flex h-5 w-5 items-center justify-center rounded-full bg-blue-500/20 text-[9px] font-bold text-blue-300">
          {c.autor ? c.autor[0] : "C"}
        </span>
        <span className="text-[10.5px] font-medium text-zinc-300">{c.autor}</span>
        <WhatsAppIcon className="h-3 w-3" />
        <span className="ml-auto text-[9.5px] text-zinc-600">{c.hora}</span>
      </div>
      <Balao>{c.mensagem}</Balao>
    </div>
  );
}

/* ---------------- resposta da IA ---------------- */
function RespostaIa({ c }) {
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-start gap-1.5">
        <img
          src={AVATAR}
          alt=""
          loading="lazy"
          decoding="async"
          className="h-6 w-6 shrink-0 rounded-full bg-black/60 object-cover"
        />
        <Balao propria>{c.resposta}</Balao>
      </div>
      {(c.selo || c.etiquetas) && (
        <div className="flex flex-wrap items-center gap-1.5">
          {c.digitando && (
            <span className="av-digitando flex items-center gap-[3px]" aria-hidden="true">
              <span /><span /><span />
            </span>
          )}
          {c.selo && (
            <span className="text-[10.5px] font-medium text-green-400/80">{c.selo}</span>
          )}
          {(c.etiquetas || []).map((e) => (
            <span
              key={e}
              className="rounded border border-green-500/25 bg-green-500/10 px-1.5 py-[1px] text-[9.5px] font-medium text-green-300/90"
            >
              {e}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

/* ---------------- lista com status (estoque, diagnóstico) ---------------- */
const STATUS = {
  ok: { cor: "34,197,94", Icone: Check, rotulo: "ok" },
  alerta: { cor: "245,158,11", Icone: AlertTriangle, rotulo: "!" },
  neutro: { cor: "125,155,190", Icone: Minus, rotulo: "—" },
};

function Checklist({ c }) {
  return (
    <ul className="flex flex-col gap-1">
      {c.itens.map((it) => {
        const s = STATUS[it.status || (it.ok === false ? "alerta" : "ok")];
        return (
          <li
            key={it.nome}
            className="flex items-center gap-1.5 rounded-md px-1.5 py-1 text-[11px]"
            style={CAIXA}
          >
            <span
              className="flex h-3.5 w-3.5 items-center justify-center rounded-full"
              style={{ background: `rgba(${s.cor},0.2)` }}
            >
              <s.Icone className="h-2.5 w-2.5" style={{ color: `rgb(${s.cor})` }} strokeWidth={3} />
            </span>
            <span className="min-w-0 flex-1 truncate text-zinc-300">{it.nome}</span>
            <span
              className="text-[10px] font-medium"
              style={{ color: `rgba(${s.cor},0.85)` }}
            >
              {it.nota || s.rotulo}
            </span>
          </li>
        );
      })}
    </ul>
  );
}

/* ---------------- agenda de horários ---------------- */
function Agenda({ c }) {
  return (
    <div className="flex flex-col gap-1">
      {c.cabecalho && (
        <div className="flex items-center justify-between text-[10px] text-zinc-500">
          <span className="truncate">{c.cabecalho}</span>
          {c.profissional && (
            <span className="shrink-0 font-medium text-zinc-400">{c.profissional}</span>
          )}
        </div>
      )}
      <div className="flex flex-wrap gap-1">
        {c.horarios.map((h) => {
          const livre = h.livre !== false;
          const alvo = h.alvo;
          return (
            <span
              key={h.hora}
              className="flex items-center gap-1 rounded-md px-1.5 py-1 text-[10.5px] font-medium"
              style={
                alvo
                  ? { background: "rgba(34,197,94,0.16)", border: "1px solid rgba(34,197,94,0.45)", color: "#86efac" }
                  : livre
                  ? { ...CAIXA, color: "#a1a1aa" }
                  : { background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.05)", color: "#52525b" }
              }
            >
              {h.hora}
              {alvo ? (
                <Check className="h-2.5 w-2.5" strokeWidth={3} />
              ) : livre ? (
                <Check className="h-2.5 w-2.5 opacity-50" strokeWidth={3} />
              ) : (
                <Minus className="h-2.5 w-2.5" strokeWidth={3} />
              )}
            </span>
          );
        })}
      </div>
    </div>
  );
}

/* ---------------- ficha (OS, confirmação, reserva, orçamento) ---------------- */
function Ficha({ c }) {
  return (
    <div className="flex flex-col gap-1 rounded-lg p-2" style={CAIXA}>
      {c.titulo && (
        <div className="flex flex-col gap-0.5">
          <span className="text-[10.5px] font-medium text-zinc-400">{c.titulo}</span>
          {c.valor && <span className="text-[15px] font-bold leading-tight text-white">{c.valor}</span>}
        </div>
      )}
      {(c.linhas || []).map((l) => (
        <div key={l.rotulo} className="flex items-baseline gap-1.5 text-[10.5px]">
          <span className="shrink-0 text-zinc-500">{l.rotulo}</span>
          <span className="min-w-0 flex-1 truncate font-medium text-zinc-300">{l.valor}</span>
        </div>
      ))}
      {(c.selo || c.status) && (
        <div className="mt-0.5 flex items-center gap-1.5">
          {c.selo && (
            <span className="rounded bg-cyan-500/15 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-cyan-300">
              {c.selo}
            </span>
          )}
          {c.status && (
            <span
              className={`flex items-center gap-1 text-[10.5px] font-medium ${
                c.pendente ? "text-amber-400" : "text-green-400"
              }`}
            >
              {c.pendente ? (
                <span className="av-pisca h-1.5 w-1.5 rounded-full bg-amber-400" />
              ) : (
                <Check className="h-3 w-3" strokeWidth={3} />
              )}
              {c.status}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

/* ---------------- métrica (painel de conexão) ---------------- */
function Metrica({ c }) {
  const alerta = c.estado === "alerta";
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center gap-1.5">
        <span className="text-[10px] uppercase tracking-wider text-zinc-500">{c.rotulo}</span>
        <span
          className="ml-auto flex items-center gap-1 rounded px-1.5 py-[1px] text-[9.5px] font-bold uppercase tracking-wider"
          style={
            alerta
              ? { background: "rgba(245,158,11,0.14)", border: "1px solid rgba(245,158,11,0.3)", color: "#fcd34d" }
              : { background: "rgba(34,197,94,0.14)", border: "1px solid rgba(34,197,94,0.3)", color: "#86efac" }
          }
        >
          {alerta && <AlertTriangle className="h-2.5 w-2.5" strokeWidth={3} />}
          {c.estado === "alerta" ? "atenção" : "normal"}
        </span>
      </div>
      <div className="flex items-baseline gap-1.5 rounded-lg px-2 py-1.5" style={CAIXA}>
        <span
          className="text-[19px] font-bold leading-none"
          style={{ color: alerta ? "#fbbf24" : "#4ade80" }}
        >
          {c.atual}
        </span>
        <span className="text-[10px] text-zinc-500">de {c.contratado}</span>
        {c.variacao && (
          <span className="ml-auto flex items-center gap-0.5 text-[10px] font-medium text-amber-400">
            <ArrowDown className="h-2.5 w-2.5" strokeWidth={3} />
            {c.variacao}
          </span>
        )}
      </div>
    </div>
  );
}

/* ---------------- progresso (ação automática) ---------------- */
function Progresso({ c }) {
  return (
    <div className="flex flex-col gap-1">
      {c.rotulo && (
        <span className="flex items-center gap-1.5 text-[10.5px] font-medium text-cyan-300/90">
          <span className="av-pisca h-1.5 w-1.5 rounded-full bg-cyan-400" aria-hidden="true" />
          {c.rotulo}
        </span>
      )}
      {c.passos.map((p) => (
        <div key={p} className="flex items-center gap-1.5 rounded-md px-1.5 py-1 text-[11px]" style={CAIXA}>
          <span className="flex h-3.5 w-3.5 items-center justify-center rounded-full bg-green-500/20">
            <Check className="h-2.5 w-2.5 text-green-400" strokeWidth={3} />
          </span>
          <span className="min-w-0 flex-1 truncate text-zinc-300">{p}</span>
        </div>
      ))}
    </div>
  );
}

/* ---------------- opções oferecidas pela IA ---------------- */
function Opcoes({ c }) {
  return (
    <div className="flex flex-col gap-1">
      {c.pergunta && <span className="text-[10.5px] text-zinc-400">{c.pergunta}</span>}
      <div className="flex flex-wrap gap-1">
        {c.opcoes.map((o) => {
          const escolhida = typeof o === "object" && o.escolhida;
          const texto = typeof o === "object" ? o.nome : o;
          return (
            <span
              key={texto}
              className="flex items-center gap-1 rounded-md px-1.5 py-1 text-[10.5px] font-medium"
              style={
                escolhida
                  ? { background: "rgba(34,197,94,0.16)", border: "1px solid rgba(34,197,94,0.45)", color: "#86efac" }
                  : { ...CAIXA, color: "#a1a1aa" }
              }
            >
              {escolhida && <Check className="h-2.5 w-2.5" strokeWidth={3} />}
              {texto}
            </span>
          );
        })}
      </div>
    </div>
  );
}

/* ---------------- decisão ---------------- */
function Decisao({ c }) {
  return (
    <div className="flex flex-col gap-1.5">
      {c.saidas.map((s) => (
        <div key={s.rotulo} className="flex items-center gap-1.5">
          <span
            className="rounded px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider"
            style={
              s.positivo
                ? { background: "rgba(34,197,94,0.16)", border: "1px solid rgba(34,197,94,0.35)", color: "#86efac" }
                : { background: "rgba(245,158,11,0.14)", border: "1px solid rgba(245,158,11,0.32)", color: "#fcd34d" }
            }
          >
            {s.rotulo}
          </span>
          <span className="min-w-0 flex-1 truncate text-[11px] text-zinc-400">{s.destino}</span>
        </div>
      ))}
    </div>
  );
}

/* ---------------- notificação para a equipe ---------------- */
function Notificacao({ c }) {
  return (
    <div className="flex flex-col gap-1.5">
      <div
        className="rounded-lg px-2 py-1.5"
        style={{ background: "rgba(168,85,247,0.10)", border: "1px solid rgba(168,85,247,0.22)" }}
      >
        <div className="text-[11px] font-semibold text-purple-200">{c.notificacao}</div>
        <div className="truncate text-[10.5px] text-zinc-400">{c.detalhe}</div>
      </div>
      <div className="flex items-center gap-1.5">
        {(c.equipe || []).length > 0 && (
          <span className="flex -space-x-1.5">
            {c.equipe.map((ini) => (
              <span
                key={ini}
                className="flex h-4 w-4 items-center justify-center rounded-full border border-[#1C2120] bg-purple-500/30 text-[8px] font-bold text-purple-100"
              >
                {ini}
              </span>
            ))}
          </span>
        )}
        <span className="av-pisca h-1.5 w-1.5 rounded-full bg-purple-400" aria-hidden="true" />
        <span className="min-w-0 truncate text-[10px] font-medium text-purple-300/90">{c.status}</span>
      </div>
    </div>
  );
}

/* ---------------- conclusão ---------------- */
function Conclusao({ c }) {
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-start gap-1.5">
        <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-green-500/20">
          <Check className="h-3.5 w-3.5 text-green-400" strokeWidth={3} />
        </span>
        <Balao propria>{c.mensagem}</Balao>
      </div>
      <div className="flex items-center gap-2">
        {c.meta && (
          <span className="text-[10px] text-zinc-500">
            {c.meta}{" "}
            {c.destaque && <span className="font-semibold text-zinc-300">{c.destaque}</span>}
          </span>
        )}
        {c.avaliacao > 0 && (
          <span className="flex items-center gap-[1px]">
            {Array.from({ length: c.avaliacao }, (_, i) => (
              <Star key={i} className="h-2.5 w-2.5 text-amber-400" fill="currentColor" />
            ))}
          </span>
        )}
      </div>
    </div>
  );
}

// Layout explícito tem prioridade; o tipo é só o padrão de cada posição.
const POR_LAYOUT = {
  conversa: Conversa,
  resposta: RespostaIa,
  checklist: Checklist,
  agenda: Agenda,
  ficha: Ficha,
  metrica: Metrica,
  progresso: Progresso,
  opcoes: Opcoes,
  decisao: Decisao,
  notificacao: Notificacao,
  conclusao: Conclusao,
};

const POR_TIPO = {
  cliente: Conversa,
  ia: RespostaIa,
  sistema: Checklist,
  decisao: Decisao,
  acao: Ficha,
  humano: Notificacao,
  sucesso: Conclusao,
};

export default function MioloNo({ etapa }) {
  const { conteudo, tipo, descricao } = etapa;

  // Fallback: etapa ainda sem mini-conteúdo.
  if (!conteudo) {
    return descricao ? (
      <p className="text-[11.5px] leading-snug text-zinc-500">{descricao}</p>
    ) : null;
  }

  const Componente = POR_LAYOUT[conteudo.layout] || POR_TIPO[tipo];
  if (!Componente) return null;
  return <Componente c={conteudo} />;
}
